import { useMemo } from "react";

import { useRoleAccess } from "@/features/auth/model/useRoleAccess";
import { useModulePermissions } from "@/features/manifest/model/useModulePermission";

import { type SettingsSectionId } from "./sections";

const ACTIVITY_ROLES = new Set([
  "OWNER",
  "ADMIN",
  "PROGRAM_DIRECTOR",
  "ACCOUNTANT",
  "STAFF",
]);

const PERMISSIONS_ROLES = new Set(["OWNER", "ADMIN"]);

const CORE_SECTIONS: SettingsSectionId[] = [
  "organisation",
  "branding",
  "donations",
  "email",
  "features",
];

export type SettingsSectionVisibilityCtx = {
  fullAccess: boolean;
  canView: (module: string) => boolean;
  hasCapability: (module: string, capability: "view" | "create" | "edit" | "delete") => boolean;
  userRole: string | undefined;
};

export function computeVisibleSettingsSections(
  ctx: SettingsSectionVisibilityCtx,
): SettingsSectionId[] {
  const visible: SettingsSectionId[] = [];
  const canCore =
    ctx.fullAccess ||
    ctx.canView("settings") ||
    ctx.hasCapability("settings", "edit");

  if (canCore) {
    visible.push(...CORE_SECTIONS);
  }

  if (
    ctx.fullAccess ||
    (ctx.canView("settings") && ctx.userRole && ACTIVITY_ROLES.has(ctx.userRole))
  ) {
    visible.push("activity");
  }

  if (
    ctx.fullAccess ||
    (ctx.userRole && PERMISSIONS_ROLES.has(ctx.userRole) && ctx.canView("settings"))
  ) {
    visible.push("permissions");
  }

  if (ctx.fullAccess || ctx.canView("data")) {
    visible.push("data");
  }

  if (ctx.fullAccess || ctx.canView("billing")) {
    visible.push("billing");
  }

  return visible;
}

export function useVisibleSettingsSections(): SettingsSectionId[] {
  const { role } = useRoleAccess();
  const { canView, hasCapability, fullAccess } = useModulePermissions();

  return useMemo(
    () =>
      computeVisibleSettingsSections({
        fullAccess,
        canView,
        hasCapability,
        userRole: role,
      }),
    [canView, fullAccess, hasCapability, role],
  );
}
